import React, { useEffect, useMemo, useState } from "react";
import SegmentedPills from "../ui/SegmentedPills";
import { api, formatEURDecimal, formatDateFR, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Plus, X, Gavel, Trash2 } from "lucide-react";
import { SkeletonTableRows } from "../ui/Skeleton";
import EmptyState from "./EmptyState";
import { confirmAction } from "../ConfirmDialog";

export const FINE_TYPE_LABELS = {
  stationnement: "Stationnement",
  exces_vitesse: "Excès de vitesse",
  feu_rouge: "Feu rouge",
  peage: "Péage",
  autre: "Autre",
};

export const FINE_STATUS_META = {
  received: { label: "Reçue", cls: "bg-blue-100 text-blue-700" },
  designated: { label: "Conducteur désigné", cls: "bg-violet-100 text-violet-700" },
  paid: { label: "Payée", cls: "bg-emerald-100 text-emerald-700" },
  contested: { label: "Contestée", cls: "bg-amber-100 text-amber-700" },
  closed: { label: "Clôturée", cls: "bg-slate-200 text-slate-600" },
};

export const ALERT_TONE_CLS = {
  warning: "bg-amber-50 border-amber-200 text-amber-800",
  negative: "bg-red-50 border-red-200 text-red-700",
};

const inputCls = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-violet-600 outline-none";
const labelCls = "text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500";

const EMPTY_FINE = { vehicle_id: "", fine_type: "stationnement", offense_date: "", offense_location: "", fine_number: "", authority: "", points: "", amount: "", increased_amount: "", payment_due_date: "", designation_due_date: "", notes: "" };

export default function GlobalFines({ navigate, vehicles, fixedVehicleId = null }) {
  const [fines, setFines] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(() => ({ ...EMPTY_FINE, vehicle_id: fixedVehicleId || "" }));
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/vendor/fines").then((r) => setFines(r.data.fines || [])).catch(() => setFines([]));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const vid = fixedVehicleId || form.vehicle_id;
    if (!vid) { toast.error("Sélectionnez un véhicule"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/vendor/fines", {
        ...form, vehicle_id: vid,
        points: form.points === "" ? null : parseInt(form.points, 10),
        amount: form.amount === "" ? null : parseFloat(form.amount),
        increased_amount: form.increased_amount === "" ? null : parseFloat(form.increased_amount),
        payment_due_date: form.payment_due_date || null,
        designation_due_date: form.designation_due_date || null,
      });
      toast.success(`Contravention ${data.fine.reference} enregistrée`);
      if (fixedVehicleId) { setForm({ ...EMPTY_FINE, vehicle_id: fixedVehicleId }); setShowForm(false); load(); }
      else navigate(`/vendor/pilotage?tab=vehicles&fine=${data.fine.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
    setSaving(false);
  };

  const remove = async (f) => {
    const ok = await confirmAction({
      title: "Supprimer définitivement cette contravention ?",
      message: `La contravention ${f.reference} sera supprimée de la flotte et de la fiche véhicule. Cette action est irréversible.`,
      confirmLabel: "Supprimer définitivement", variant: "danger", requireText: "SUPPRIMER",
    });
    if (!ok) return;
    try {
      await api.delete(`/vendor/fines/${f.id}`);
      toast.success("Contravention supprimée"); load();
      window.dispatchEvent(new Event("taskcenter-changed"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Erreur"); }
  };

  const filtered = useMemo(() => {
    if (!fines) return [];
    let list = fixedVehicleId ? fines.filter((f) => f.vehicle_id === fixedVehicleId) : fines;
    if (statusFilter !== "all") list = list.filter((f) => f.status === statusFilter);
    return list;
  }, [fines, statusFilter, fixedVehicleId]);

  const alertCount = (fines || []).reduce((s, f) => s + (f.alerts || []).length, 0);

  if (fines === null) return <SkeletonTableRows rows={5} cols={5} testid="fines-skeleton" />;

  return (
    <div className="space-y-4" data-testid="global-fines">
      {alertCount > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="fines-alerts-strip">
          {(fines || []).flatMap((f) => (f.alerts || []).map((a, i) => (
            <button
              key={`${f.id}-${i}`}
              onClick={() => navigate(`/vendor/pilotage?tab=vehicles&fine=${f.id}`)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[11px] font-semibold hover:brightness-95 transition-all ${ALERT_TONE_CLS[a.tone] || ALERT_TONE_CLS.warning}`}
              data-testid={`fine-alert-${f.reference}-${a.kind}`}
            >
              {a.icon} {a.label} · {f.reference} <span className="font-normal opacity-80 hidden sm:inline">— {a.detail}</span>
            </button>
          )))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <SegmentedPills
          value={statusFilter}
          onChange={setStatusFilter}
          items={[
            { key: "all", label: "Toutes", count: fines.length },
            { key: "received", label: "Reçues", count: fines.filter((f) => f.status === "received").length },
            { key: "designated", label: "Désignées", count: fines.filter((f) => f.status === "designated").length },
            { key: "paid", label: "Payées", count: fines.filter((f) => f.status === "paid").length },
            { key: "contested", label: "Contestées", count: fines.filter((f) => f.status === "contested").length },
            { key: "closed", label: "Clôturées", count: fines.filter((f) => f.status === "closed").length },
          ]}
          testid="fines-filter"
        />
        <button
          onClick={() => setShowForm(!showForm)}
          className="ml-auto inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700"
          data-testid="add-fine-btn"
        >
          {showForm ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />} {showForm ? "Fermer" : "Nouvelle contravention"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="rounded-2xl border border-violet-200 bg-violet-50/30 p-4 grid sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="fine-form">
          {!fixedVehicleId && (
          <div>
            <label className={labelCls}>Véhicule *</label>
            <select value={form.vehicle_id} onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })} required className={inputCls + " mt-1"} data-testid="fine-vehicle">
              <option value="">Sélectionner…</option>
              {vehicles.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
          )}
          <div>
            <label className={labelCls}>Type d'infraction</label>
            <select value={form.fine_type} onChange={(e) => setForm({ ...form, fine_type: e.target.value })} className={inputCls + " mt-1"} data-testid="fine-type">
              {Object.entries(FINE_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Date d'infraction *</label>
            <input type="date" value={form.offense_date} onChange={(e) => setForm({ ...form, offense_date: e.target.value })} required className={inputCls + " mt-1"} data-testid="fine-date" />
          </div>
          <div>
            <label className={labelCls}>Lieu de l'infraction</label>
            <input value={form.offense_location} onChange={(e) => setForm({ ...form, offense_location: e.target.value })} placeholder="Paris 15e, A6…" className={inputCls + " mt-1"} data-testid="fine-location" />
          </div>
          <div>
            <label className={labelCls}>N° de contravention</label>
            <input value={form.fine_number} onChange={(e) => setForm({ ...form, fine_number: e.target.value })} className={inputCls + " mt-1"} data-testid="fine-number" />
          </div>
          <div>
            <label className={labelCls}>Autorité émettrice</label>
            <input value={form.authority} onChange={(e) => setForm({ ...form, authority: e.target.value })} placeholder="ANTAI, Ville de Paris…" className={inputCls + " mt-1"} data-testid="fine-authority" />
          </div>
          <div>
            <label className={labelCls}>Montant (€)</label>
            <input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={inputCls + " mt-1"} data-testid="fine-amount" />
          </div>
          <div>
            <label className={labelCls}>Montant majoré (€)</label>
            <input type="number" min="0" step="0.01" value={form.increased_amount} onChange={(e) => setForm({ ...form, increased_amount: e.target.value })} className={inputCls + " mt-1"} data-testid="fine-increased" />
          </div>
          <div>
            <label className={labelCls}>Points concernés (info)</label>
            <input type="number" min="0" max="12" value={form.points} onChange={(e) => setForm({ ...form, points: e.target.value })} className={inputCls + " mt-1"} data-testid="fine-points" />
          </div>
          <div>
            <label className={labelCls}>Date limite de paiement</label>
            <input type="date" value={form.payment_due_date} onChange={(e) => setForm({ ...form, payment_due_date: e.target.value })} className={inputCls + " mt-1"} data-testid="fine-payment-due" />
          </div>
          <div>
            <label className={labelCls}>Date limite de désignation</label>
            <input type="date" value={form.designation_due_date} onChange={(e) => setForm({ ...form, designation_due_date: e.target.value })} className={inputCls + " mt-1"} data-testid="fine-designation-due" />
          </div>
          <div>
            <label className={labelCls}>Notes</label>
            <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls + " mt-1"} data-testid="fine-notes" />
          </div>
          <div className="sm:col-span-3 lg:col-span-4 flex justify-end">
            <button type="submit" disabled={saving} className="h-[38px] px-6 rounded-xl bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="fine-submit">
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Enregistrer la contravention
            </button>
          </div>
        </form>
      )}

      {filtered.length === 0 ? (
        <EmptyState
          icon={Gavel}
          title="Aucune contravention"
          description={statusFilter !== "all" ? "Aucune contravention pour ce filtre." : "Les amendes reçues et leur désignation de conducteur apparaîtront ici."}
          testid="fines-empty"
        />
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">Référence</th>
                  <th className="text-left px-5 py-3 font-semibold">Type</th>
                  <th className="text-left px-5 py-3 font-semibold">Véhicule</th>
                  <th className="text-left px-5 py-3 font-semibold">Infraction</th>
                  <th className="text-left px-5 py-3 font-semibold">Conducteur</th>
                  <th className="text-left px-5 py-3 font-semibold">Statut</th>
                  <th className="text-right px-5 py-3 font-semibold">Montant</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((f) => {
                  const st = FINE_STATUS_META[f.status] || FINE_STATUS_META.received;
                  const go = () => navigate(`/vendor/pilotage?tab=vehicles&fine=${f.id}`);
                  return (
                    <tr
                      key={f.id}
                      className="hover:bg-violet-50/40 transition-colors"
                      data-testid={`fine-row-${f.reference}`}
                    >
                      <td className="px-5 py-3.5 whitespace-nowrap font-semibold text-slate-900 cursor-pointer" onClick={go}>
                        {f.reference}
                        {(f.alerts || []).length > 0 && <span className="ml-1.5" title={f.alerts.map((a) => a.label).join(" · ")}>{f.alerts[0].icon}</span>}
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap text-slate-600 cursor-pointer" onClick={go}>{FINE_TYPE_LABELS[f.fine_type] || f.fine_type}</td>
                      <td className="px-5 py-3.5 max-w-[160px] truncate text-slate-600">{f.vehicle_name}</td>
                      <td className="px-5 py-3.5 whitespace-nowrap text-slate-500 text-xs">{formatDateFR(f.offense_date)}{f.offense_location ? ` · ${f.offense_location}` : ""}</td>
                      <td className="px-5 py-3.5 max-w-[140px] truncate text-slate-600">{f.driver_name || (f.suggested_booking ? `${f.suggested_booking.client_name} ?` : "—")}</td>
                      <td className="px-5 py-3.5"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${st.cls}`}>{st.label}</span></td>
                      <td className="px-5 py-3.5 text-right font-semibold text-slate-900">{f.amount != null ? formatEURDecimal(f.amount) : "—"}</td>
                      <td className="px-5 py-3.5 text-right">
                        <button onClick={(e) => { e.stopPropagation(); remove(f); }} className="h-8 w-8 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 inline-flex items-center justify-center" title="Supprimer définitivement" data-testid={`fine-delete-${f.id}`}><Trash2 className="h-4 w-4" /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
